import { Box, Button, Divider, Stack, TextField, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { colors } from '~/theme';
import { CameraScanner, isScanButtonAvailable } from '~/components/camera-scanner';
import { BackLink } from '~/components/back-link/back-link';
import { validateBillingOrigin } from '~/api/methods';
import { LoadingScreen } from '~/screens/loading/loading';
import { ResultScreen } from '~/screens/result/result';
import { resolvePixInput } from './validate-pix.state';

type Step = 'input' | 'scanning' | 'loading' | 'success' | 'failure';

export const ValidatePixScreen = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('input');
  const [pixPayload, setPixPayload] = useState('');
  const [formatError, setFormatError] = useState(false);

  const submit = async (rawInput: string) => {
    const resolved = resolvePixInput(rawInput);

    if (resolved.kind === 'formatError') {
      setFormatError(true);
      return;
    }

    setFormatError(false);
    setStep('loading');

    try {
      const response = await validateBillingOrigin({ pixPayload: resolved.normalized });
      setStep(response.isValid ? 'success' : 'failure');
    } catch {
      setStep('failure');
    }
  };

  if (step === 'loading') {
    return <LoadingScreen />;
  }

  if (step === 'success' || step === 'failure') {
    return (
      <ResultScreen
        variant={step === 'success' ? 'success' : 'failure'}
        subject="pix"
        onNewValidation={() => navigate('/onboarding')}
      />
    );
  }

  if (step === 'scanning') {
    return (
      <Box height="70vh">
        <CameraScanner
          formats={['QRCode']}
          onDetect={(value) => {
            setStep('input');
            setPixPayload(value);
            submit(value);
          }}
          onClose={() => setStep('input')}
        />
      </Box>
    );
  }

  return (
    <Stack spacing={3} px={3} py={4}>
      <BackLink />

      <Divider sx={{ borderColor: colors.gray }} />

      <Stack spacing={1}>
        <Typography variant="h1" fontWeight={700} sx={{ fontSize: '16px', color: colors.charcoal }}>
          VALIDAR CÓDIGO PIX
        </Typography>
        <Typography variant="body2" sx={{ color: colors.darkGray }}>
          Cole o código Pix no campo abaixo ou escaneie o QR code
        </Typography>
      </Stack>

      {isScanButtonAvailable() && (
        <Button variant="outlined" size="large" fullWidth onClick={() => setStep('scanning')}>
          ESCANEAR QR CODE
        </Button>
      )}

      <TextField
        label="Pix Copia e Cola"
        placeholder="Cole aqui o código Pix copia e cola"
        multiline
        minRows={4}
        value={pixPayload}
        onChange={(event) => {
          setPixPayload(event.target.value);
          setFormatError(false);
        }}
        error={formatError}
        helperText={formatError ? 'Código inválido. Confira e tente novamente.' : undefined}
      />

      <Button variant="contained" size="large" fullWidth onClick={() => submit(pixPayload)}>
        VALIDAR
      </Button>
    </Stack>
  );
};
